import { t } from "@lingui/core/macro"
import { AlertTriangle, Home, RefreshCw } from "lucide-react"
import { ShelfiIcon } from "@/components/ShelfiLogo"
import { Card } from "@/components/ui/card"
import { IconButton } from "./ui/iconbutton"

type ErrorComponentProps = {
  error: Error
  reset?: () => void
  eventId?: string
}

export function ErrorComponent({ error, reset, eventId }: ErrorComponentProps) {
  const handleRetry = () => {
    if (reset) {
      reset()
    } else {
      // Fallback: reload the page
      window.location.reload()
    }
  }

  const handleGoToDashboard = () => {
    // Use window.location for reliable navigation from error state
    // This works even when router context might be corrupted
    window.location.href = "/"
  }

  return (
    <div className="from-brand-darkblue-50 to-brand-cyan-50 flex min-h-screen items-center justify-center bg-gradient-to-br via-white p-4">
      <Card className="border-brand-darkblue-150 relative w-full max-w-md overflow-hidden bg-white p-8 text-center shadow-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="bg-brand-darkblue-500 absolute -top-10 -right-10 h-40 w-40 rounded-full" />
          <div className="bg-brand-cyan-500 absolute -bottom-10 -left-10 h-40 w-40 rounded-full" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Icon Section */}
          <div className="mb-6">
            <div className="from-error-50 to-brand-orange-50 mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br shadow-lg">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white">
                <AlertTriangle className="text-error-500 h-8 w-8" strokeWidth={2.5} />
              </div>
            </div>

            <div className="mb-4 flex items-center justify-center gap-2">
              <ShelfiIcon size={32} className="text-brand-darkblue-500" />
              <div className="bg-brand-darkblue-150 h-8 w-px" />
              <h1 className="text-brand-darkblue-500 text-2xl font-bold">
                {t`Something went wrong`}
              </h1>
            </div>

            <p className="text-neutral-700">
              {t`We encountered an error while processing your request. Don't worry, we'll help you get back on track.`}
            </p>
          </div>

          {/* Error Details */}
          <div className="border-error-50 bg-error-50/50 mb-6 rounded-lg border p-4 text-left">
            <h3 className="text-error-900 mb-2 text-sm font-semibold tracking-wide uppercase">
              {t`Error details`}
            </h3>
            <code className="block rounded bg-white px-3 py-2 font-mono text-xs break-words text-neutral-700">
              {error.message || t`An unexpected error occurred`}
            </code>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <IconButton
              onClick={handleRetry}
              variant="default"
              icon={<RefreshCw />}
              label={t`Try Again`}
            >
              {t`Try Again`}
            </IconButton>

            <IconButton
              variant="secondary"
              onClick={handleGoToDashboard}
              icon={<Home />}
              label={t`Go to Dashboard`}
            >
              {t`Go to Dashboard`}
            </IconButton>
          </div>

          {/* Footer */}
          <div className="mt-6 space-y-2 text-xs text-neutral-500">
            <p>{t`If this problem persists, please contact our support team.`}</p>
            {eventId && (
              <div className="rounded-full bg-neutral-50 px-3 py-1">
                {t`Error ID:`} <code className="font-semibold text-neutral-700">{eventId}</code>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
